import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useSnackbar } from "notistack";
import Layout from "../components/layout/Layout";
import ShopList from "../components/shops/ShopList";
import ShopForm from "../components/shops/ShopForm";
import DefaultHoursEditor from "../components/shops/DefaultHoursEditor";
import DefaultCoverageEditor from "../components/shops/DefaultCoverageEditor";
import ShopCoverageCalendar from "../components/availability/ShopCoverageCalendar";
import { useShops } from "../hooks/useShops";
import { useOrganization } from "../context/OrganizationContext";
import { Shop, WeeklyCoverage } from "../types/shop.types";
import { WeeklyHours } from "../types/common.types";

interface ShopsPageProps {
  view?: "coverage" | "info";
}

export default function ShopsPage({ view = "info" }: ShopsPageProps) {
  const { canWrite, organization } = useOrganization();
  const allowEditHistory = organization?.settings?.allowEditHistory ?? false;
  const {
    shops,
    isLoading,
    createShop,
    updateShop,
    deleteShop,
    updateDefaultHours,
    updateDefaultCoverage,
    isCreating,
    isUpdating,
    isDeleting,
  } = useShops();
  const { enqueueSnackbar } = useSnackbar();

  const [formOpen, setFormOpen] = useState(false);
  const [hoursEditorOpen, setHoursEditorOpen] = useState(false);
  const [coverageEditorOpen, setCoverageEditorOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);

  const handleCreateShop = async (data: {
    name: string;
    address?: string;
    contactInfo?: { phone?: string; email?: string };
    defaultHours: WeeklyHours;
  }) => {
    try {
      await createShop(data);
      enqueueSnackbar("Shop created successfully!", { variant: "success" });
      setFormOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create shop";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleUpdateShop = async (data: {
    name: string;
    address?: string;
    contactInfo?: { phone?: string; email?: string };
    defaultHours: WeeklyHours;
  }) => {
    if (!selectedShop) return;

    try {
      await updateShop(selectedShop.id, data);
      enqueueSnackbar("Shop updated successfully!", { variant: "success" });
      setFormOpen(false);
      setSelectedShop(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update shop";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleDeleteShop = async () => {
    if (!selectedShop) return;

    try {
      await deleteShop(selectedShop.id);
      enqueueSnackbar("Shop deleted successfully!", { variant: "success" });
      setDeleteDialogOpen(false);
      setSelectedShop(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete shop";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleSaveHours = async (hours: WeeklyHours) => {
    if (!selectedShop) return;

    try {
      await updateDefaultHours(selectedShop.id, hours);
      enqueueSnackbar("Hours updated successfully!", { variant: "success" });
      setHoursEditorOpen(false);
      setSelectedShop(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update hours";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleEditShop = (shop: Shop) => {
    setSelectedShop(shop);
    setFormOpen(true);
  };

  const handleViewHours = (shop: Shop) => {
    setSelectedShop(shop);
    setHoursEditorOpen(true);
  };

  const handleDeleteClick = (shop: Shop) => {
    setSelectedShop(shop);
    setDeleteDialogOpen(true);
  };

  const handleConfigureCoverage = (shop: Shop) => {
    setSelectedShop(shop);
    setCoverageEditorOpen(true);
  };

  const handleSaveCoverage = async (coverage: WeeklyCoverage) => {
    if (!selectedShop) return;

    try {
      await updateDefaultCoverage(selectedShop.id, coverage);
      enqueueSnackbar("Coverage requirements saved successfully!", {
        variant: "success",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to save coverage requirements";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleAddShop = () => {
    setSelectedShop(null);
    setFormOpen(true);
  };

  return (
    <Layout
      pageTitle={view === "coverage" ? "Shop Coverage" : "Shop Information"}
    >
      <Box>
        {/* Coverage View */}
        {view === "coverage" && (
          <ShopCoverageCalendar
            shops={shops}
            isLoading={isLoading}
            canWrite={canWrite}
            allowEditHistory={allowEditHistory}
          />
        )}

        {/* Shop Information View */}
        {view === "info" && (
          <ShopList
            shops={shops}
            isLoading={isLoading}
            canWrite={canWrite}
            onEdit={handleEditShop}
            onDelete={handleDeleteClick}
            onViewHours={handleViewHours}
            onConfigureCoverage={handleConfigureCoverage}
            onAddShop={handleAddShop}
          />
        )}

        {/* Shop Form Dialog */}
        <ShopForm
          open={formOpen}
          shop={selectedShop}
          onClose={() => {
            setFormOpen(false);
            setSelectedShop(null);
          }}
          onSubmit={selectedShop ? handleUpdateShop : handleCreateShop}
          isLoading={isCreating || isUpdating}
        />

        {/* Default Hours Editor */}
        {selectedShop && (
          <DefaultHoursEditor
            open={hoursEditorOpen}
            defaultHours={selectedShop.defaultHours}
            shopName={selectedShop.name}
            onClose={() => {
              setHoursEditorOpen(false);
              setSelectedShop(null);
            }}
            onSave={handleSaveHours}
            isLoading={isUpdating}
            allShops={shops}
            currentShopId={selectedShop.id}
          />
        )}

        {/* Default Coverage Editor */}
        {selectedShop && (
          <DefaultCoverageEditor
            open={coverageEditorOpen}
            shop={selectedShop}
            shops={shops}
            onClose={() => {
              setCoverageEditorOpen(false);
              setSelectedShop(null);
            }}
            onSave={handleSaveCoverage}
            isLoading={isUpdating}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Shop</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{selectedShop?.name}"? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteShop}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
